import { useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import alertsUtil from '@/util/alertsUtil';
import { dialogTypes, snackbarTypes } from '@/constants/alertTypes';
import { UpdateCodeVisualizationTemplate } from '@experiment-management-shared';
import { updateCodePanelTemplate } from './api';
import { PanelTemplate, TemplateRevision } from './types';
import { TEMPLATE_SCOPE_TYPES } from '@/experiment-management-shared/constants/chartsGallery';
import { useActiveWorkspace } from '@shared/hooks';
import { AxiosError, AxiosResponse } from 'axios';

interface CustomErrorResponse {
  msg: string;
}

interface UpdateCodeTemplateError extends AxiosError {
  response?: AxiosResponse<CustomErrorResponse>;
}

const useUpdateCodeTemplate = (template?: PanelTemplate) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const activeWorkspace = useActiveWorkspace();
  return useMutation<
    TemplateRevision,
    UpdateCodeTemplateError,
    UpdateCodeVisualizationTemplate
  >(
    (payload: UpdateCodeVisualizationTemplate) =>
      updateCodePanelTemplate({
        ...template,
        ...payload,
        lastVersion: template?.revisionId
      }),
    {
      onSuccess: async (data, variables) => {
        await queryClient.invalidateQueries([
          'panelTemplate',
          { templateId: template?.templateId }
        ]);
        await queryClient.invalidateQueries([
          'panelTemplates',
          { teamId: activeWorkspace?.id, scope: TEMPLATE_SCOPE_TYPES.WORKSPACE }
        ]);
        await queryClient.invalidateQueries([
          'panelTemplateRevisions',
          { templateId: template?.templateId }
        ]);

        dispatch(
          alertsUtil.openSnackbarDialog(
            snackbarTypes.SUCCESS_API_RESPONSE,
            `You successfully updated '${variables.templateName}'`
          )
        );
      },
      onError: ({ response }) => {
        dispatch(
          alertsUtil.openErrorDialog(
            dialogTypes.CATCH_ERROR_API,
            response?.data?.msg ||
              'An error occurred while updating the code template'
          )
        );
      }
    }
  );
};

export default useUpdateCodeTemplate;
